<template>
  <div class="accordion" id="accordionPanel">
    <div
      v-if="showProgressBar"
      class="progress rounded-bottom-0"
      :class="{ 'rounded-0': !roundedCorners }"
      role="progressbar"
      aria-label="Basic example"
      aria-valuenow="0"
      aria-valuemin="0"
      aria-valuemax="100"
    >
      <div
        class="progress-bar"
        :style="{ width: `${progressPercentage}%` }"
      ></div>
    </div>
    <div
      :key="section.title"
      v-for="(section, index) in sections"
      class="accordion-item"
      :class="{ 'rounded-0': !roundedCorners }"
    >
      <h2
        class="accordion-header"
        :id="getHeadingId(index)"
        :class="{ 'rounded-0': !roundedCorners }"
      >
        <button
          class="accordion-button collapsed"
          :class="{ 'rounded-0': !roundedCorners }"
          type="button"
          data-bs-toggle="collapse"
          :data-bs-target="`#${getSectionId(index)}`"
          :aria-expanded="false"
          :aria-controls="`${getSectionId(index)}`"
        >
          {{ section.title }}
        </button>
      </h2>
      <div
        :id="getSectionId(index)"
        :ref="(el) => (sectionRefs[index] = el)"
        class="accordion-collapse collapse"
        :aria-labelledby="`${getHeadingId(index)}`"
        data-bs-parent="#accordionPanel"
      >
        <div
          :id="`${getSectionId(index)}-body`"
          class="accordion-body text-start p-3"
        >
          <p v-if="section.body">
            {{ section.body }}
          </p>
          <transition name="fade">
            <div
              class="alert alert-warning p-2"
              v-if="section.warning && showSectionWarning(index)"
              v-html="section.warning"
            ></div>
          </transition>
          <slot
            name="sectionTemplate"
            :section-index="index"
            v-bind="section"
          ></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Collapse } from "bootstrap";

/**
 * The reactive data driving the steps within a section.
 * @typedef {Object} Step
 * @property {string} message - The message of the step
 * @property {boolean} completed - Whether the step has been completed
 *
 * The reactive data driving the sections of the activity.
 * @typedef {Object} Section
 * @property {string} title - The title of the section
 * @property {string} [body] - The body text of the section
 * @property {string} [instructions] - Instructions for the section
 * @property {Step[]} steps - The steps of the section
 */

export default {
  name: "AccordionPanel",

  props: {
    /**
     * @type {string}
     */
    title: String,

    /**
     * @type {Section[]}
     */
    sections: {
      type: Array,
      required: true
    },

    /**
     * @type {number}
     */
    activeSectionIndex: {
      type: Number,
      required: true
    },

    /**
     * @type {boolean}
     */
    showProgressBar: Boolean,

    /**
     * @type {boolean}
     */
    roundedCorners: Boolean,

    /**
     * A function that determines whether to show the warning for a section.
     * @type {Function (index: number) => boolean}
     */
    showSectionWarning: {
      type: Function,
      default: () => true
    }
  },

  data() {
    return {
      /**
       * @type {HTMLElement[]}
       */
      sectionRefs: []
    };
  },

  computed: {
    /**
     * @returns {number}
     */
    progressPercentage() {
      return (this.activeSectionIndex + 1 / this.sections.length) * 10;
    }
  },

  methods: {
    /**
     * @param {number} index
     * @returns {string}
     */
    getSectionId(index) {
      return `section-${index}`;
    },

    /**
     * @param {number} index
     * @returns {string}
     */
    getHeadingId(index) {
      return `heading-${index}`;
    },

    /**
     * @param {number} index
     */
    showSection(index) {
      const section = this.sectionRefs[index];
      if (!section) return;
      const bsCollapse = Collapse.getOrCreateInstance(section);
      bsCollapse?.show();
    },

    /**
     * @param {number} index
     */
    hideSection(index) {
      const section = this.sectionRefs[index];
      if (!section) return;
      const bsCollapse = Collapse.getOrCreateInstance(section);
      bsCollapse?.hide();
    }
  },

  watch: {
    activeSectionIndex: {
      handler(newIndex, oldIndex) {
        this.hideSection(oldIndex);
        this.showSection(newIndex);
      }
    }
  },

  mounted() {
    // Show current active section on mount
    this.showSection(this.activeSectionIndex);
  }
};
</script>

<style scoped>
.accordion {
  max-height: calc(100vh - var(--header-height) - var(--footer-height));
  overflow-y: auto;
}
/* Transition classes for fade effect */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
